import React, { useState, useEffect, useCallback } from "react";
import {
  useNavigate
} from "react-router-dom";
import {
  Plus,
  Pencil,
  UserX,
  Bus
} from "lucide-react";
import {
  Button,
  IconButton,
  Input,
  Select,
  Table,
  Badge,
  EmptyState,
  PageHeader,
  Tooltip
} from "../../components/ui";
import AssignmentModal from "../../components/transport/AssignmentModal";
import {
  getAllAssignments,
  getAllAssignmentsPortal,
  deactivateAssignment,
  deactivateAssignmentPortal,
  getAcademicYears,
  getAcademicYearsPortal,
  getClasses,
  getClassesPortal,
  getSections
} from "../../api/transport/transportApi";
import {
  useToast
} from "../../context/ToastContext";

/**
 * List of existing transport assignments (view / edit / deactivate).
 * New assignments are done on the separate Assign Transport page.
 */
const StudentTransportAssignmentList = ({ isPortal = false }) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    academicYearId: "",
    classId: "",
    sectionId: "",
    status: "ACTIVE",
    search: "",
  });

  const [yearOptions, setYearOptions] = useState([]);
  const [classOptions, setClassOptions] = useState([]);
  const [sectionOptions, setSectionOptions] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const toast = useToast();

  useEffect(() => {
    (async () => {
      try {
        const [yRes, cRes] = await Promise.all([
          isPortal ? getAcademicYearsPortal() : getAcademicYears(),
          isPortal ? getClassesPortal() : getClasses(),
        ]);
        const years = yRes.data || yRes || [];
        const classes = cRes.data || cRes || [];
        setYearOptions(
          years.map((y) => ({
            value: y._id,
            label: y.yearName || y.name || String(y._id),
          }))
        );
        setClassOptions(
          classes.map((c) => ({
            value: c._id,
            label: c.className || c.name || String(c._id),
          }))
        );
        const active = years.find((y) => y.isActive);
        if (active) {
          setFilters((p) => ({ ...p, academicYearId: active._id }));
        } else if (years[0]) {
          setFilters((p) => ({ ...p, academicYearId: years[0]._id }));
        }
      } catch {
        /* ignore */
      }
    })();
  }, [isPortal]);

  useEffect(() => {
    if (!filters.classId) {
      setSectionOptions([]);
      return;
    }
    (async () => {
      try {
        const res = await getSections({ classId: filters.classId });
        const list = res.data || res || [];
        setSectionOptions(
          list.map((s) => ({
            value: s._id,
            label: s.sectionName || s.name || String(s._id),
          }))
        );
      } catch {
        setSectionOptions([]);
      }
    })();
  }, [filters.classId]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.academicYearId) params.academicYearId = filters.academicYearId;
      if (filters.classId) params.classId = filters.classId;
      if (filters.sectionId) params.sectionId = filters.sectionId;
      if (filters.status) params.status = filters.status;
      if (filters.search) params.search = filters.search;

      const res = isPortal
        ? await getAllAssignmentsPortal(params)
        : await getAllAssignments(params);
      setRows(res.data || res || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load assignments");
    } finally {
      setLoading(false);
    }
  }, [isPortal, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDeactivate = async (id) => {
    if (!window.confirm("Deactivate this transport assignment?")) return;
    setBusyId(id);
    try {
      if (isPortal) await deactivateAssignmentPortal(id);
      else await deactivateAssignment(id);
      toast.success("Assignment deactivated.");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Deactivate failed");
    } finally {
      setBusyId(null);
    }
  };

  const goToAssignPage = () => {
    navigate(
      isPortal
        ? "/staff-panel/transport/assign"
        : "/transport/assign"
    );
  };

  const columns = [
    {
      key: "student",
      header: "Student",
      render: (row) => {
        const s = row.studentId || {};
        const name = `${s.firstName || ""} ${s.lastName || ""}`.trim() || "—";
        return (
          <div>
            <div className="font-medium text-neutral-900 dark:text-neutral-100">{name}</div>
            <div className="text-xs text-neutral-500 dark:text-neutral-400">
              {s.admissionNo || "—"}
              {s.rollNumber ? ` · Roll ${s.rollNumber}` : ""}
            </div>
          </div>
        );
      },
    },
    {
      key: "class",
      header: "Class / Section",
      render: (row) => {
        const s = row.studentId || {};
        const cls = s.classId?.className || s.classId?.classCode || "—";
        const sec = s.sectionId?.sectionName || "—";
        return (
          <span>
            {cls} / {sec}
          </span>
        );
      },
    },
    {
      key: "vehicle",
      header: "Vehicle",
      render: (row) => {
        const v = row.vehicleId || {};
        return (
          <div>
            <div>{v.name || "—"}</div>
            <div className="text-xs text-neutral-500 dark:text-neutral-400 font-mono">
              {v.vehicleNumber}
            </div>
          </div>
        );
      },
    },
    {
      key: "pincode",
      header: "Pincode",
      render: (row) => row.pincode || "—",
    },
    {
      key: "monthlyCharge",
      header: "Monthly ₹",
      render: (row) => (
        <span className="font-medium">
          ₹{Number(row.monthlyCharge || 0).toFixed(2)}
        </span>
      ),
    },
    {
      key: "seatNumber",
      header: "Seat",
      render: (row) => (row.seatNumber != null ? row.seatNumber : "—"),
    },
    {
      key: "status",
      header: "Status",
      render: (row) => {
        const v =
          row.status === "ACTIVE"
            ? "success"
            : row.status === "SUSPENDED"
              ? "warning"
              : "default";
        return <Badge variant={v}>{row.status}</Badge>;
      },
    },
    {
      key: "actions",
      header: "Actions",
      render: (row) => (
        <div className="flex items-center justify-end gap-1">
          <Tooltip content="Edit">
            <IconButton
              aria-label="Edit assignment"
              variant="ghost"
              onClick={() => {
                setEditData(row);
                setModalOpen(true);
              }}
            >
              <Pencil className="h-4 w-4" />
            </IconButton>
          </Tooltip>
          {row.status === "ACTIVE" && (
            <Tooltip content="Deactivate">
              <IconButton
                aria-label="Deactivate assignment"
                variant="ghost"
                disabled={busyId === row._id}
                onClick={() => handleDeactivate(row._id)}
              >
                <UserX className="h-4 w-4 text-danger-500" />
              </IconButton>
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-4">
      <PageHeader
        title="Student Transport Assignments"
        description="View and manage existing assignments. Use Assign Transport to add new ones."
        actions={
          <Button leftIcon={<Plus className="h-4 w-4" />} onClick={goToAssignPage}>
            Assign transport
          </Button>
        }
      />

      
      

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <Select
          label="Academic year"
          options={yearOptions}
          value={filters.academicYearId}
          onChange={(v) => setFilters((p) => ({ ...p, academicYearId: v }))}
          placeholder="All years"
        />
        <Select
          label="Class"
          options={[{ label: "All classes", value: "" }, ...classOptions]}
          value={filters.classId}
          onChange={(v) =>
            setFilters((p) => ({ ...p, classId: v, sectionId: "" }))
          }
          placeholder="All classes"
          searchable
        />
        <Select
          label="Section"
          options={[{ label: "All sections", value: "" }, ...sectionOptions]}
          value={filters.sectionId}
          onChange={(v) => setFilters((p) => ({ ...p, sectionId: v }))}
          placeholder="All sections"
          disabled={!filters.classId}
          searchable
        />
        <Select
          label="Status"
          options={[
            { label: "Active", value: "ACTIVE" },
            { label: "Inactive", value: "INACTIVE" },
            { label: "Suspended", value: "SUSPENDED" },
            { label: "All", value: "" },
          ]}
          value={filters.status}
          onChange={(v) => setFilters((p) => ({ ...p, status: v }))}
        />
        <Input
          label="Search"
          placeholder="Name, admission no..."
          value={filters.search}
          onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))}
        />
      </div>

      <Table
        columns={columns}
        data={rows}
        isLoading={loading}
        emptyState={
          <EmptyState
            icon={Bus}
            title="No transport assignments"
            description="Go to Assign Transport to assign vehicles to students."
            action={
              <Button
                size="sm"
                leftIcon={<Plus className="h-4 w-4" />}
                onClick={goToAssignPage}
              >
                Assign transport
              </Button>
            }
          />
        }
      />

      <AssignmentModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditData(null);
        }}
        onSuccess={() => {
          toast.success("Assignment updated.");
          fetchData();
        }}
        editData={editData}
        student={editData?.studentId || null}
        academicYearId={filters.academicYearId}
        academicYearOptions={yearOptions}
        isPortal={isPortal}
      />
    </div>
  );
};

export default StudentTransportAssignmentList;
